import { Notice } from '../types';

type CacheEntry<T> = {
  data: T | null;
  timestamp: number;
  isLoading: boolean;
  error: string | null;
};

type CacheConfig = {
  duration: number; // Cache duration in milliseconds
  maxRetries: number;
};

/**
 * Simple cache implementation for notices
 * Prevents multiple API calls and provides centralized cache management
 */
class NoticesCache {
  private cache: CacheEntry<Notice[]>;

  private config: CacheConfig;

  private subscribers: Set<() => void>;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = {
      duration: 5 * 60 * 1000, // 5 minutes default
      maxRetries: 3,
      ...config
    };

    this.cache = {
      data: null,
      timestamp: 0,
      isLoading: false,
      error: null
    };

    this.subscribers = new Set();
  }

  /**
   * Subscribe to cache changes
   */
  subscribe(callback: () => void): () => void {
    this.subscribers.add(callback);

    // Return unsubscribe function
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Notify all subscribers of cache changes
   */
  private notify(): void {
    this.subscribers.forEach((callback) => callback());
  }

  /**
   * Check if cached data is still valid
   */
  private isValid(): boolean {
    if (!this.cache.data) return false;

    const now = Date.now();
    return now - this.cache.timestamp < this.config.duration;
  }

  /**
   * Get cached data if valid
   */
  get(): Notice[] | null {
    return this.isValid() ? this.cache.data : null;
  }

  /**
   * Get cache status
   */
  getStatus(): { isLoading: boolean; error: string | null; hasData: boolean } {
    return {
      isLoading: this.cache.isLoading,
      error: this.cache.error,
      hasData: this.cache.data !== null
    };
  }

  /**
   * Set cache data
   */
  set(data: Notice[]): void {
    this.cache = {
      data,
      timestamp: Date.now(),
      isLoading: false,
      error: null
    };
    this.notify();
  }

  /**
   * Set loading state
   */
  setLoading(isLoading: boolean): void {
    this.cache.isLoading = isLoading;
    if (isLoading) {
      this.cache.error = null;
    }
    this.notify();
  }

  /**
   * Set error state
   */
  setError(error: string): void {
    this.cache.error = error;
    this.cache.isLoading = false;
    this.notify();
  }

  /**
   * Clear cache
   */
  clear(): void {
    this.cache = {
      data: null,
      timestamp: 0,
      isLoading: false,
      error: null
    };
    this.notify();
  }

  /**
   * Check if currently loading
   */
  isLoading(): boolean {
    return this.cache.isLoading;
  }
}

// Create singleton instance
export const noticesCache = new NoticesCache();

// Export type for external use
export type { CacheConfig };
